import React, { useRef } from "react";
import clsx from "clsx";
import styles from "./ConfigureLbx.module.scss";
import ConfigureNavigation from "./ConfigureNavigation";
import Container from "../../../../shared-components/css-modules/lexus/atoms/Container";
import { CarConfigConfigureSectionsLbx } from "../../../utils/types/SectionTypesLbx";
import Atmosphere from "./sections/atmosphere/Atmosphere";
import Powertrain from "./sections/powertrain/Powertrain";
import Packs from "./sections/packs/Packs";
import ExtrasWrapper from "./sections/extras/ExtrasWrapper";
import Services from "./sections/services/Services";
import { useServicesSectionVisibility } from "../../../hooks/useSectionVisibility";
import { useAphStepTracking } from "../../../hooks/useAphStepTracking";
import useSectionIntersectionObserver from "../../../hooks/useSectionIntersectionObserver";
import { useDebouncedEffect } from "../../../../../../common-deprecated/hooks";
import { useCarConfigSelector, type CarConfigDispatchType } from "../../../redux/store";
import { useComponentRegistry } from "../componentRegistry";
import { completeUnfinishedConfiguration, getLocalStorageParams } from "../../../utils/localStorage";
import SectionWrapper from "../common/section-wrapper/SectionWrapper";
import { setActiveSpin, SpinnerType } from "../../../redux/slices/CarConfigSpinNavigationSlice";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const ConfigureLbx: React.FC = (): JSX.Element => {
    const dispatch = useDispatch<CarConfigDispatchType>();
    const desktopContainerRef = useRef<HTMLDivElement | null>(null);

    const activeSection = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const localStorageParams = useCarConfigSelector(getLocalStorageParams);

    const showPacks = useCarConfigSelector((state) => state.items.packs.length !== 0);
    const showExtras = useCarConfigSelector((state) => state.items.extras.some((item) => !item.isHidden));

    const showServices = useServicesSectionVisibility();

    const sections = Object.values(CarConfigConfigureSectionsLbx).filter((section) => {
        switch (section) {
            case CarConfigConfigureSectionsLbx.Services:
                return showServices;
            case CarConfigConfigureSectionsLbx.Packs:
                return showPacks;
            case CarConfigConfigureSectionsLbx.Extras:
                return showExtras;
            default:
                return true;
        }
    });
    const sectionRefs = new Map(sections.map((section) => [section, React.createRef<HTMLElement>()]));

    const trackStep = useAphStepTracking();
    const activeSpin = useCarConfigSelector((state) => state.carConfigSpinNavigation.activeSpin);

    useSectionIntersectionObserver<CarConfigConfigureSectionsLbx>(sectionRefs);
    useDebouncedEffect(
        () => {
            trackStep(activeSection);
            const isUpholsterySection = activeSection === CarConfigConfigureSectionsLbx.AtmosphereUpholstery;
            const targetSpinType = isUpholsterySection ? SpinnerType.Upholstery : SpinnerType.Car;

            if (activeSpin !== targetSpinType) {
                dispatch(setActiveSpin(targetSpinType));
            }
            if (activeSection === CarConfigConfigureSectionsLbx.Summary) {
                completeUnfinishedConfiguration(localStorageParams);
            }
        },
        500,
        [activeSection],
    );

    const Summary = useComponentRegistry("Summary");

    return (
        <div id="tab-configure">
            <main
                data-testid="configure-lbx-step"
                className={clsx(styles.configure, styles.loaded)}
                ref={desktopContainerRef}
                id="carconfig-scroll-container"
                data-activesection={activeSection}
            >
                <Atmosphere sectionRefs={sectionRefs} />
                {showPacks && <Packs sectionRefs={sectionRefs} />}
                {showExtras && <ExtrasWrapper sectionRefs={sectionRefs} />}
                <Powertrain sectionRefs={sectionRefs} />
                {showServices && <Services sectionRefs={sectionRefs} />}
                <ConfigureNavigation
                    sections={sections}
                    activeSection={activeSection as CarConfigConfigureSectionsLbx}
                />
                <SectionWrapper
                    sectionId={CarConfigConfigureSectionsLbx.Summary}
                    sectionRef={sectionRefs.get(CarConfigConfigureSectionsLbx.Summary)}
                    hasToggle
                >
                    <Container>
                        <Summary />
                    </Container>
                </SectionWrapper>
            </main>
        </div>
    );
};

export default ConfigureLbx;
